import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Dna, GitFork, Users } from 'lucide-react';
import { AppLayout } from '@/components/Layout/AppLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { PersonAutocomplete } from '@/components/PersonForm/PersonAutocomplete';
import { MedicalTab } from '@/components/PersonPanel/tabs/MedicalTab';
import { ApiError } from '@/api/client';
import {
  getInheritance,
  type AncestorEntry,
  type InheritanceData,
  type InheritancePersonLite,
} from '@/api/medical.api';
import { useConditions, useTraits } from '@/hooks/useMedical';
import { usePerson } from '@/hooks/usePersons';
import {
  CATEGORY_LABELS,
  TRAIT_TYPE_BY_KEY,
  formatTraitValue,
} from '@/components/PersonPanel/tabs/medical/medicalLabels';

// ─── Hilfsfunktionen ────────────────────────────────────────────────────────

function formatName(p: InheritancePersonLite): string {
  return `${p.firstName} ${p.lastName ?? ''}`.trim();
}

function lifeDates(p: InheritancePersonLite): string | null {
  if (!p.birthDateText && !p.deathDateText) return p.isLiving ? 'lebend' : null;
  return `${p.birthDateText ?? '?'} – ${p.deathDateText ?? (p.isLiving ? 'lebend' : '?')}`;
}

/** Überschrift je Generation: 1 = Eltern, 2 = Großeltern, 3 = Urgroßeltern, … */
function generationLabel(generation: number): string {
  if (generation === 1) return 'Eltern';
  if (generation === 2) return 'Großeltern';
  if (generation === 3) return 'Urgroßeltern';
  if (generation === 4) return 'Ururgroßeltern';
  // Ab hier genealogie-übliche Kurzform (konsistent zu den Karten-Labels)
  return `${generation - 2}×Urgroßeltern`;
}

const SIDE_BADGE: Record<'paternal' | 'maternal', { label: string; className: string }> = {
  paternal: { label: 'väterliche Linie', className: 'bg-sky-50 text-sky-700' },
  maternal: { label: 'mütterliche Linie', className: 'bg-rose-50 text-rose-700' },
};

// ─── Merkmals-/Diagnose-Chips einer Person ──────────────────────────────────

function MedicalChips({ personId }: { personId: string }) {
  const { data: traits, isLoading: traitsLoading, isError: traitsError, refetch: refetchTraits } = useTraits(personId);
  const { data: condResp, isLoading: condLoading, isError: condError, refetch: refetchConds } = useConditions(personId);

  if (traitsLoading || condLoading) {
    return <p className="text-xs text-slate-400">Gesundheitsdaten werden geladen…</p>;
  }

  // WICHTIG: Ladefehler dürfen nicht wie "keine Befunde" aussehen –
  // in einem Medizin-Kontext wäre das eine echte Fehlinformation.
  if (traitsError || condError) {
    return (
      <p className="text-xs text-red-600">
        Gesundheitsdaten konnten nicht geladen werden.{' '}
        <button
          type="button"
          onClick={() => { void refetchTraits(); void refetchConds(); }}
          className="underline hover:text-red-700"
        >
          Erneut versuchen
        </button>
      </p>
    );
  }

  const conditions = condResp?.data ?? [];
  const hereditaryHidden = condResp?.hereditaryHidden ?? 0;
  const hasAnything = (traits?.length ?? 0) > 0 || conditions.length > 0 || hereditaryHidden > 0;

  if (!hasAnything) {
    return <p className="text-xs text-slate-400">Keine Angaben.</p>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {/* Merkmale, z. B. "Augenfarbe: Blau", "Körpergröße: 178 cm" */}
      {traits?.map((t) => (
        <span
          key={t.id}
          className="rounded-full bg-teal-50 px-2 py-0.5 text-xs text-teal-800"
          title={t.notes ?? undefined}
        >
          {TRAIT_TYPE_BY_KEY[t.traitType]?.label ?? t.traitType}: {formatTraitValue(t)}
        </span>
      ))}

      {/* Diagnosen mit Kategorie-Badge */}
      {conditions.map((c) => {
        const label = c.catalog?.label ?? c.freeText ?? 'Ohne Bezeichnung';
        const category = c.catalog ? CATEGORY_LABELS[c.catalog.category] ?? c.catalog.category : null;
        return (
          <span
            key={c.id}
            className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-800"
            title={c.notes ?? undefined}
          >
            {label}
            {category && (
              <span className="rounded bg-white/70 px-1 py-px text-[10px] font-medium text-red-600">
                {category}
              </span>
            )}
          </span>
        );
      })}

      {/* Ausgeblendete sensible Einträge */}
      {hereditaryHidden > 0 && (
        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
          {hereditaryHidden} {hereditaryHidden === 1 ? 'Eintrag' : 'Einträge'} ausgeblendet (sensibel)
        </span>
      )}
    </div>
  );
}

// ─── Expandierter Pflegebereich (kompletter MedicalTab) ─────────────────────

function MedicalEditor({ personId }: { personId: string }) {
  const { data: person, isLoading, isError } = usePerson(personId);

  if (isLoading) {
    return <LoadingSpinner size="sm" message="Personendaten werden geladen…" />;
  }
  if (isError || !person) {
    return <p className="py-3 text-sm text-red-500">Personendaten konnten nicht geladen werden.</p>;
  }
  return <MedicalTab person={person} />;
}

// ─── Vorfahren-Karte ────────────────────────────────────────────────────────

interface AncestorCardProps {
  person: InheritancePersonLite;
  /** z. B. "Großmutter (väterlicherseits)" – bei der Ausgangsperson leer */
  relationLabel?: string;
  side?: 'paternal' | 'maternal' | null;
  expanded: boolean;
  onToggle: () => void;
}

function AncestorCard({ person, relationLabel, side, expanded, onToggle }: AncestorCardProps) {
  const dates = lifeDates(person);
  const sideBadge = side ? SIDE_BADGE[side] : null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-start gap-3 rounded-lg p-3 text-left transition-colors hover:bg-slate-50 sm:p-4"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-medium text-slate-800">{formatName(person)}</span>
            {relationLabel && <span className="text-xs text-slate-500">{relationLabel}</span>}
            {sideBadge && (
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${sideBadge.className}`}>
                {sideBadge.label}
              </span>
            )}
          </div>
          {dates && <p className="mt-0.5 text-xs text-slate-400">{dates}</p>}
          <div className="mt-2">
            <MedicalChips personId={person.id} />
          </div>
        </div>
        <span className="mt-0.5 flex shrink-0 items-center gap-1 text-xs text-slate-500">
          {expanded ? 'Schließen' : 'Pflegen'}
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-slate-200 p-3 sm:p-4">
          <MedicalEditor personId={person.id} />
        </div>
      )}
    </div>
  );
}

// ─── Ausgangsperson-Karte ───────────────────────────────────────────────────

function RootCard({
  person,
  expanded,
  onToggle,
}: {
  person: InheritancePersonLite;
  expanded: boolean;
  onToggle: () => void;
}) {
  const navigate = useNavigate();
  const dates = lifeDates(person);

  return (
    <div className="rounded-lg border border-slate-300 bg-white shadow-sm">
      <div className="flex flex-wrap items-start gap-3 p-3 sm:p-4">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Ausgangsperson</p>
          <p className="mt-0.5 text-lg font-semibold text-slate-800">{formatName(person)}</p>
          {dates && <p className="text-xs text-slate-400">{dates}</p>}
          <div className="mt-2">
            <MedicalChips personId={person.id} />
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <button
            type="button"
            onClick={() => navigate(`/?person=${person.id}`)}
            className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
          >
            <GitFork className="h-4 w-4" />
            Im Stammbaum zeigen
          </button>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
          >
            {expanded ? 'Schließen' : 'Pflegen'}
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-200 p-3 sm:p-4">
          <MedicalEditor personId={person.id} />
        </div>
      )}
    </div>
  );
}

// ─── Seite ──────────────────────────────────────────────────────────────────

const GENERATION_OPTIONS = [2, 3, 4, 5, 6, 7, 8, 9, 10];

export function InheritancePage() {
  const [personId, setPersonId] = useState<string | null>(null);
  const [generations, setGenerations] = useState(4);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const {
    data: inheritance,
    isLoading,
    isError,
    error,
  } = useQuery<InheritanceData, ApiError>({
    queryKey: ['medical', 'inheritance', personId, generations],
    queryFn: () => getInheritance(personId as string, generations),
    enabled: !!personId,
  });

  const toggle = (id: string) => setExpandedId((cur) => (cur === id ? null : id));

  const handlePersonChange = (id: string | null) => {
    setPersonId(id);
    setExpandedId(null);
  };

  const isForbidden = isError && error instanceof ApiError && error.status === 403;

  // Vorfahren nach Generation gruppieren (Reihenfolge der Generationen aufsteigend)
  const byGeneration = new Map<number, AncestorEntry[]>();
  for (const a of inheritance?.ancestors ?? []) {
    const list = byGeneration.get(a.generation) ?? [];
    list.push(a);
    byGeneration.set(a.generation, list);
  }
  const generationNumbers = [...byGeneration.keys()].sort((a, b) => a - b);

  return (
    <AppLayout>
      <div className="h-full overflow-auto p-4 sm:p-6">
        <div className="mx-auto max-w-4xl">
          {/* Kopf */}
          <div className="mb-1 flex items-center gap-2">
            <Dna className="h-5 w-5 text-slate-600" />
            <h1 className="text-2xl font-bold text-slate-800">Vererbung</h1>
          </div>
          <p className="mb-6 text-xs text-slate-400">
            Vorfahren einer Person über mehrere Generationen mit ihren Merkmalen und Gesundheitsdaten –
            zum Erkennen und Pflegen familiärer Muster. Zugriffe auf Gesundheitsdaten werden protokolliert.
            Keine medizinische Diagnose; ersetzt keine humangenetische Beratung.
          </p>

          {/* Suche + Generationen-Auswahl */}
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-slate-500">Ausgangsperson</label>
              <PersonAutocomplete
                value={personId}
                onChange={handlePersonChange}
                placeholder="Ausgangsperson suchen…"
                aria-label="Ausgangsperson suchen"
              />
            </div>
            <div>
              <label htmlFor="inheritance-generations" className="mb-1 block text-xs font-medium text-slate-500">
                Generationen
              </label>
              <select
                id="inheritance-generations"
                value={generations}
                onChange={(e) => setGenerations(Number(e.target.value))}
                className="rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900
                           focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              >
                {GENERATION_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n} Generationen
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Leerzustand ohne Auswahl */}
          {!personId && (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
              <Users className="mx-auto mb-2 h-8 w-8 text-slate-300" />
              <p className="text-sm text-slate-500">
                Ausgangsperson auswählen, um deren Vorfahren und Gesundheitsbild zu sehen.
              </p>
            </div>
          )}

          {/* Laden / Fehler */}
          {personId && isLoading && (
            <div className="flex justify-center py-16">
              <LoadingSpinner size="lg" message="Vorfahren werden ermittelt…" />
            </div>
          )}
          {personId && isForbidden && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
              Keine Berechtigung für das Medizin-Modul. Die Vorfahren-Ansicht erfordert Zugriff auf
              Gesundheitsdaten – bitte an eine Administratorin/einen Administrator wenden.
            </p>
          )}
          {personId && isError && !isForbidden && (
            <p className="text-center text-sm text-red-500">
              Vorfahren konnten nicht geladen werden{error instanceof ApiError && error.message ? `: ${error.message}` : '.'}
            </p>
          )}

          {/* Ergebnis */}
          {personId && inheritance && (
            <div className="space-y-6">
              <RootCard
                person={inheritance.root}
                expanded={expandedId === inheritance.root.id}
                onToggle={() => toggle(inheritance.root.id)}
              />

              {inheritance.ancestors.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center">
                  <p className="text-sm text-slate-500">
                    Keine Vorfahren erfasst. Eltern lassen sich über das Personen-Panel im Stammbaum verknüpfen.
                  </p>
                </div>
              ) : (
                generationNumbers.map((gen) => (
                  <section key={gen}>
                    <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-slate-500">
                      <Users className="h-4 w-4" />
                      {generationLabel(gen)}
                      <span className="font-normal normal-case text-slate-400">
                        · Generation {gen}
                      </span>
                    </h2>
                    <div className="space-y-2">
                      {byGeneration.get(gen)!.map((ancestor) => (
                        <AncestorCard
                          key={ancestor.id}
                          person={ancestor}
                          relationLabel={ancestor.relationLabel}
                          side={ancestor.side}
                          expanded={expandedId === ancestor.id}
                          onToggle={() => toggle(ancestor.id)}
                        />
                      ))}
                    </div>
                  </section>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

export default InheritancePage;
